import { ExternalLinkIcon } from '@radix-ui/react-icons';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Link,
  ScrollArea,
  Text,
} from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { HelmetIcon } from 'components/common/custom-icon/shorthands';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HINT_DELIMITER } from 'components/common/form/text-hint';
import { HitterSideBadge } from 'components/common/hitters/hitter-side-badge';
import { PitcherHandBadge } from 'components/common/pitcher-hand-badge';
import {
  AnnotatedVideoPlayer,
  getFireVideoID,
  IAnnotationDetails,
} from 'components/common/video/annotated-player';
import env from 'config';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import {
  MachineSettingsQueryParams,
  MachineSettingsSection,
} from 'enums/machine-settings';
import { t } from 'i18next';
import { StringHelper } from 'lib_ts/classes/string.helper';
import {
  LOCAL_DATETIME_FORMAT_LONG,
  LOCAL_TIMEZONE,
} from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IBPSummary } from 'lib_ts/interfaces/i-bp-summary';
import { FC, useCallback, useMemo, useState } from 'react';
import { paths } from 'routes/paths';

interface IProps {
  summary: IBPSummary;
}

export const BpSummaryCard: FC<IProps> = (props) => {
  const { summary } = props;
  const { history } = summary;

  const subtitles = useMemo(
    () =>
      [summary.nickname, summary.teamName, summary.competition]
        .filter((m) => m)
        .join(HINT_DELIMITER),
    [summary.nickname, summary.teamName, summary.competition]
  );

  const [dialogVideo, setDialogVideo] = useState<
    {
      key: number;
      subtitle: string;
    } & IAnnotationDetails
  >();

  const getLinkUrl = useCallback(
    (fireID: string, shotID: string | null | undefined) => {
      const params = shotID
        ? // link to shot in shot history
          new URLSearchParams({
            [MachineSettingsQueryParams.MachineID]: summary.machineID,
            [MachineSettingsQueryParams.SectionID]:
              MachineSettingsSection.ShotHistory,
            [MachineSettingsQueryParams.ShotID]: shotID,
          })
        : // link to fire in live fires, for fallback when shot hasn't been created (yet)
          new URLSearchParams({
            [MachineSettingsQueryParams.MachineID]: summary.machineID,
            [MachineSettingsQueryParams.SectionID]:
              MachineSettingsSection.LiveFires,
            [MachineSettingsQueryParams.FireID]: fireID,
          });

      return `${env.self_url}${paths.admin.machines}?${params.toString()}`;
    },
    [summary.machineID]
  );

  return (
    <ErrorBoundary identifier={`BpSummaryCard${summary.machineID}`}>
      <Card size="3">
        <Flex justify="between" mb="4">
          {/* header + subtitles */}
          <Box>
            <Flex align="center" gap="2">
              <Heading size="6">{summary.machineID}</Heading>
              {summary.inGame && (
                <Badge color={RADIX.COLOR.SUCCESS}>
                  <HelmetIcon /> In-Game
                </Badge>
              )}
            </Flex>

            <Text size="1" color={RADIX.COLOR.SECONDARY}>
              {subtitles}

              {summary.user && (
                <>
                  {HINT_DELIMITER}
                  <Link
                    title={t('common.active-user').toString()}
                    href={`mailto:${summary.user}`}
                    color={RADIX.COLOR.INFO}
                    size="1"
                  >
                    {summary.user}
                  </Link>
                </>
              )}
            </Text>
          </Box>
        </Flex>

        <ScrollArea style={{ height: '20vh' }}>
          <Flex direction="column" gap="2">
            {history.length === 0 && (
              <Text>There are no BP fires at this time.</Text>
            )}

            {history.map((m) => {
              const fireCreated = parseISO(m.fireCreated);

              const videoID = getFireVideoID({
                annotated_video_id: m.annotated_video_id,
                replay_video_id: m.replay_video_id,
              });

              return (
                <Card
                  key={`fire-${m.fireID}`}
                  size="1"
                  style={{ opacity: 1 }}
                  className={StringHelper.classNames([
                    'no-background',
                    videoID ? 'cursor-pointer' : undefined,
                  ])}
                  title={
                    videoID
                      ? 'View video playback'
                      : 'There is no video data at this time'
                  }
                  onClick={(e) => {
                    e.stopPropagation();

                    if (!videoID) {
                      NotifyHelper.warning({
                        message_md: 'There is no video data at this time',
                      });
                      return;
                    }

                    setDialogVideo({
                      key: Date.now(),
                      subtitle: [m.pitchType, m.pitchName, m.hitterName]
                        .filter((m) => m)
                        .join(HINT_DELIMITER),
                      hitterID: m.hitterID,
                      fireID: m.fireID,
                      shotID: m.shotID,
                      videoID: videoID,
                    });
                  }}
                >
                  <Flex gap="2">
                    <Avatar
                      size="4"
                      fallback={m.pitchType ?? '??'}
                      color={videoID ? RADIX.COLOR.INFO : RADIX.COLOR.NEUTRAL}
                    />

                    <Flex direction="column" justify="between" flexGrow="1">
                      <Flex align="center" gap="1" title="Pitcher">
                        {m.pitchHand && (
                          <PitcherHandBadge value={m.pitchHand} />
                        )}
                        <Heading size="3">{m.pitchName}</Heading>
                      </Flex>

                      {m.hitterName && (
                        <Flex align="center" gap="1" title="Hitter">
                          {m.hitterSide && (
                            <HitterSideBadge value={m.hitterSide} />
                          )}
                          <Heading size="3">{m.hitterName}</Heading>
                        </Flex>
                      )}
                    </Flex>

                    <Flex direction="column" justify="between" align="end">
                      <Flex gap="2">
                        <Text
                          color={RADIX.COLOR.NEUTRAL}
                          title={format(
                            fireCreated,
                            LOCAL_DATETIME_FORMAT_LONG,
                            {
                              timeZone: LOCAL_TIMEZONE,
                            }
                          )}
                          size="1"
                        >
                          {formatDistanceToNow(fireCreated)} ago
                        </Text>

                        <Text
                          title={
                            m.shotID
                              ? 'View in Shot History'
                              : 'View in Live Fires'
                          }
                          color={
                            m.shotID ? RADIX.COLOR.ACCENT : RADIX.COLOR.NEUTRAL
                          }
                          size="1"
                          onClick={(e) => {
                            e.stopPropagation();

                            const url = getLinkUrl(m.fireID, m.shotID);
                            window.open(url, '_blank');
                          }}
                        >
                          <ExternalLinkIcon />
                        </Text>
                      </Flex>

                      {m.inGame && (
                        <Text title="In-Game" color={RADIX.COLOR.SUCCESS}>
                          <HelmetIcon />
                        </Text>
                      )}
                    </Flex>
                  </Flex>
                </Card>
              );
            })}
          </Flex>
        </ScrollArea>
      </Card>

      {dialogVideo && (
        <CommonDialog
          key={dialogVideo.key}
          identifier="BpSummaryVideo"
          title="common.video-playback"
          titleActions={
            dialogVideo.fireID && (
              <Button
                variant={RADIX.BUTTON.VARIANT.SECONDARY}
                onClick={() => {
                  if (!dialogVideo.fireID) {
                    return;
                  }

                  const url = getLinkUrl(
                    dialogVideo.fireID,
                    dialogVideo.shotID
                  );

                  window.open(url, '_blank');
                }}
              >
                <ExternalLinkIcon />
                {dialogVideo.shotID
                  ? 'View in Shot History'
                  : 'View in Live Fires'}
              </Button>
            )
          }
          subtitle={
            <Heading size="2" color={RADIX.COLOR.NEUTRAL}>
              {dialogVideo.subtitle}
            </Heading>
          }
          content={<AnnotatedVideoPlayer details={dialogVideo} />}
          width={RADIX.DIALOG.WIDTH.LG}
          onClose={() => setDialogVideo(undefined)}
        />
      )}
    </ErrorBoundary>
  );
};
